/**
 * Which piece of the file to fetch next.
 *
 * The file is cut into chunks at GOP boundaries: a GOP starts with a keyframe,
 * so it is the smallest piece that decodes on its own, and cutting there means
 * every chunk is independently useful the moment it lands. The cuts run end to
 * end rather than only covering the video samples, so the chunks put back
 * together are the whole file — which is what the waveform needs.
 *
 * Order is what stops the strip jumping. Fetching from the front and letting the
 * user watch frames appear somewhere they are not looking is the same wasted
 * wait as fetching nothing; the chunks under the viewport go first, then the
 * ones either side of it, then the rest. Re-sorting on zoom or scroll costs a
 * comparison per chunk and is the whole mechanism for "follow where they look".
 *
 * Every function here is pure — no network, no decoding. What it imports is the
 * arithmetic the rest of the pipeline already agrees on, so that "nearest frame"
 * and "which tile is this" mean one thing rather than two.
 */

import { frameIndexAt } from '@src/services/mediaExtractor';
import { tileTimeAt } from '@src/services/timeline';

/**
 * Chunk boundaries from a video's keyframes, covering `[0, totalSize)`.
 *
 * Each chunk runs from one keyframe's first byte to the next keyframe's, so it
 * carries that GOP's video samples plus whatever audio is interleaved with them.
 * The head of the file (everything before the first keyframe) is its own chunk,
 * and the last one runs to the end.
 *
 * `keys` is `readSampleTable`'s `keyframes`, in decode order.
 * @returns {{ start, end, fromSec, toSec }[]}
 */
export const planChunks = (keys, totalSize, timescale, offsetSec = 0) => {
  if (!keys.length || !totalSize) return [];

  const chunks = [];
  const secOf = (sample) => sample.cts / timescale - offsetSec;
  if (keys[0].offset > 0) {
    chunks.push({
      start: 0,
      end: keys[0].offset,
      fromSec: 0,
      toSec: secOf(keys[0]),
    });
  }
  keys.forEach((key, at) => {
    const next = keys[at + 1];
    chunks.push({
      start: key.offset,
      end: next ? next.offset : totalSize,
      fromSec: secOf(key),
      toSec: next ? secOf(next) : Infinity,
    });
  });
  return chunks;
};

/**
 * How far a chunk is from the stretch on screen, in seconds. Zero while it
 * overlaps, and also zero where the two merely touch. Pure.
 */
export const chunkDistance = (chunk, fromSec, toSec) => {
  if (chunk.toSec <= fromSec) return fromSec - chunk.toSec;
  if (chunk.fromSec >= toSec) return chunk.fromSec - toSec;
  return 0;
};

/**
 * Whether any of the chunk is actually on screen. Distance alone cannot say: a
 * chunk ending exactly where the viewport begins is zero seconds away and holds
 * nothing the viewer can see, so it must not outrank one they are looking at.
 * Pure.
 */
export const chunkOnScreen = (chunk, fromSec, toSec) =>
  chunk.fromSec < toSec && chunk.toSec > fromSec;

/**
 * The chunks still missing, in the order they are wanted.
 *
 * `needed` — the moments the tiles on screen are waiting for — comes first, and
 * it is what makes this work at all. Ordering by nearness to the viewport does
 * nothing at Fit zoom, where the viewport is the whole video and every chunk is
 * equally "on screen": the tie falls through to the index and the file is
 * fetched front to back, which is exactly the wait the ordering was meant to
 * avoid. A dozen tiles spread across an hour need a dozen chunks spread across
 * it, not the first dozen.
 *
 * What is left over follows, nearest the viewport first, so the background fill
 * still starts where the eye is.
 *
 * @param {object[]} chunks from planChunks
 * @param {number[]} needed seconds the visible tiles are waiting for
 * @param {(chunk: object, index: number) => boolean} isHeld
 * @returns {number[]} indices into `chunks`
 */
export const orderChunks = (
  chunks,
  { needed = [], fromSec, toSec, isHeld },
) => {
  const wanted = new Set();
  needed.forEach((seconds) => {
    const at = chunks.findIndex(
      (chunk) => seconds >= chunk.fromSec && seconds < chunk.toSec,
    );
    if (at >= 0) wanted.add(at);
  });
  return chunks
    .map((chunk, index) => ({ chunk, index }))
    .filter(({ chunk, index }) => !isHeld(chunk, index))
    .sort((a, b) => {
      const byWanted =
        Number(!wanted.has(a.index)) - Number(!wanted.has(b.index));
      if (byWanted) return byWanted;
      if (wanted.has(a.index)) return a.index - b.index;
      const byScreen =
        Number(!chunkOnScreen(a.chunk, fromSec, toSec)) -
        Number(!chunkOnScreen(b.chunk, fromSec, toSec));
      const byDistance =
        chunkDistance(a.chunk, fromSec, toSec) -
        chunkDistance(b.chunk, fromSec, toSec);
      return byScreen || byDistance || a.index - b.index;
    })
    .map(({ index }) => index);
};

/**
 * The seconds a strip of one frame every `everySec` should hold — every step
 * inside `[0, durationSec)`. What is missing from this list, after the cache has
 * been read back, is what still has to be decoded. Pure.
 */
export const stripSeconds = (durationSec, everySec) => {
  const out = [];
  for (let sec = 0; sec < durationSec; sec += everySec) out.push(sec);
  return out;
};

/**
 * The moment a tile will show — decided before anything is fetched, so it never
 * changes as frames arrive.
 *
 * This is what stops the strip jumping. Showing whichever frame happens to be
 * nearest means every tile changes picture each time a closer one lands: at Fit
 * zoom the whole strip flickers through the opening seconds of the video before
 * settling. Fixing the target up front means a tile shows a skeleton and then
 * its frame, once.
 *
 * Which target depends on the zoom. A tile wider than a GOP cannot show the
 * difference between the keyframe and the second next to it — 4.2s inside a 66s
 * tile is a sixteenth of a cell — and a keyframe is a tenth of the bytes of the
 * run around it, so coarse tiles aim at keyframes and fine ones at the strip's
 * own grid. Pure.
 */
export const targetSecFor = ({
  tileSec,
  tileSpanSec,
  durationSec,
  keyTimes,
  gridSec,
  gopSec,
  gridComplete = false,
}) => {
  // The grid second nearest `tileSec` — clamped onto the strip's own grid.
  // stripSeconds holds every step inside [0, durationSec), so plain rounding
  // overshoots it at the end of the clip whenever the duration sits at or past
  // the half-step: a 15.000s video rounds its last tiles (centres 14.7, 14.9)
  // to second 15, which no strip ever holds — and a target no frame can match
  // is a tile on a skeleton for good.
  const gridStep = () => {
    const lastStep =
      durationSec > 0 ? Math.ceil(durationSec / gridSec) - 1 : Infinity;
    return Math.min(Math.round(tileSec / gridSec), lastStep) * gridSec;
  };

  // A grid already held in full answers every tile at every zoom, and closer
  // than a keyframe can: rounding to the nearest grid step is at most half a
  // step out, a keyframe up to half a GOP. Keyframes are only worth aiming at
  // while the grid is still filling, where they buy a picture for a tenth of the
  // bytes — once it is complete, aiming at one aims at the single thing that
  // would still have to be fetched.
  if (gridComplete && gridSec > 0) {
    return gridStep();
  }
  if (tileSpanSec <= gopSec || !keyTimes.length) {
    // No grid to snap to. The <video> fallback samples at its own irregular
    // marks and reports no `gridSec` at all, and `Math.round(tileSec / 0) * 0`
    // is NaN — a target no frame can ever match, so every tile sat on a
    // skeleton for good however many frames that path had captured. With no
    // grid the tile stands for its own moment and the lane takes the nearest
    // frame to it.
    if (!(gridSec > 0)) return tileSec;
    return gridStep();
  }
  return keyTimes[frameIndexAt(keyTimes, tileSec)];
};

/**
 * The tiles covering `[fromSec, toSec]`, each with the one moment it will show.
 *
 * The lane and the loader both need this, and they must agree exactly: the lane
 * shows a skeleton until the frame for a tile's target arrives, so a loader
 * working from a target of its own leaves that tile blank for good. They ran the
 * same arithmetic separately and drifted — one anchored its grid at the left of
 * the clip, the other at the left of the viewport — so it lives here once.
 *
 * Tiles are laid out from the start of the clip, so a tile's moment is fixed by
 * its index; scrolling slides the window over that grid rather than moving it.
 * `index` is what the lane positions by, `tileSec` the middle of the stretch it
 * covers, `target` the frame it waits for. Pure.
 */
export const viewTiles = ({
  fromSec,
  toSec,
  durationSec,
  tileSpanSec,
  keyTimes,
  gridSec,
  gopSec,
  gridComplete = false,
  max = Infinity,
}) => {
  if (!(tileSpanSec > 0) || !(durationSec > 0)) return [];
  const count = Math.max(1, Math.ceil(durationSec / tileSpanSec));
  const first = Math.max(0, Math.floor(fromSec / tileSpanSec));
  const last = Math.min(count - 1, Math.ceil(toSec / tileSpanSec));
  const tiles = [];
  for (let index = first; index <= last && tiles.length < max; index += 1) {
    const tileSec = tileTimeAt({
      index,
      tileWidth: tileSpanSec,
      stripWidth: durationSec,
      duration: durationSec,
    });
    tiles.push({
      index,
      tileSec,
      target: targetSecFor({
        tileSec,
        tileSpanSec,
        durationSec,
        keyTimes,
        gridSec,
        gopSec,
        gridComplete,
      }),
    });
  }
  return tiles;
};
